/**
 * WorkOrderSignOffPage
 * --------------------
 * Public, token-gated page for hospital staff to approve or dispute completed
 * work. No login required — the token in the URL authenticates the request.
 * Route: `/work-orders/sign-off/:token`
 *
 * Hospital sees ONLY:
 *   - title, description, completion %, checklist
 *   - external updates (internal updates never reach this view)
 *   - admin's customer-facing summary
 * They NEVER see contractor / supplier quotes or invoices.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle2,
  XCircle,
  Loader2,
  ShieldCheck,
  Hammer,
  AlertTriangle,
  Paperclip,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function WorkOrderSignOffPage() {
  const { token } = useParams();
  const [wo, setWo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signedName, setSignedName] = useState("");
  const [signedTitle, setSignedTitle] = useState("");
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [decided, setDecided] = useState(null);

  const fetchWo = async () => {
    try {
      const res = await axios.get(`${API}/api/public/work-orders/sign-off/${token}`);
      setWo(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "This link is invalid or expired.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submit = async (decision) => {
    if (!signedName.trim()) {
      toast.error("Please enter your name to sign");
      return;
    }
    if (decision === "dispute" && !feedback.trim()) {
      toast.error("Please tell us what's wrong so we can fix it");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/public/work-orders/sign-off/${token}/decision`, {
        decision,
        signed_name: signedName,
        signed_title: signedTitle,
        feedback: decision === "dispute" ? feedback : null,
      });
      setDecided(res.data.decision);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="bg-white rounded-xl border border-rose-200 p-8 max-w-md text-center">
          <AlertTriangle className="w-10 h-10 text-rose-500 mx-auto mb-3" />
          <h1 className="text-lg font-bold text-slate-900">Link unavailable</h1>
          <p className="text-sm text-slate-600 mt-2">{error}</p>
          <p className="text-xs text-slate-500 mt-4">Contact Sterile Mate at <a href="mailto:george.broughton@sterilemate.com" className="text-blue-600">george.broughton@sterilemate.com</a></p>
        </div>
      </div>
    );
  }
  if (decided) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div
          className={`bg-white rounded-xl border p-8 max-w-md text-center ${
            decided === "approve" ? "border-emerald-200" : "border-amber-200"
          }`}
        >
          {decided === "approve" ? (
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
          ) : (
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          )}
          <h1 className="text-xl font-bold text-slate-900">
            {decided === "approve" ? "Thank you — work signed off" : "Feedback received"}
          </h1>
          <p className="text-sm text-slate-600 mt-2">
            {decided === "approve"
              ? "We've notified Sterile Mate and closed your work order."
              : "We've notified Sterile Mate. They'll reach out shortly to resolve the issue."}
          </p>
        </div>
      </div>
    );
  }

  const completionPct = wo.completion_pct ?? 0;
  const checklist = wo.checklist || [];
  const updates = wo.updates || [];

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-5">
        {/* Header */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
            <span className="text-xs font-semibold tracking-wider uppercase text-emerald-700">
              Sign-off requested
            </span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">{wo.title || wo.description || "Completed work"}</h1>
          <p className="text-xs text-slate-500 font-mono mt-1">{wo.id}</p>
          {wo.hospital_name && <p className="text-sm text-slate-600 mt-2">{wo.hospital_name}</p>}

          {/* Completion bar */}
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
              <span>Completion</span>
              <span className="font-semibold text-emerald-700">{completionPct}%</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500" style={{ width: `${completionPct}%` }} />
            </div>
          </div>
        </div>

        {/* Admin summary */}
        {wo.signoff_summary && (
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h2 className="text-sm font-bold text-slate-900 mb-2 flex items-center gap-2">
              <Hammer className="w-4 h-4 text-blue-600" /> Summary of work performed
            </h2>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{wo.signoff_summary}</p>
            {wo.completion_certificate?.work_performed && (
              <div className="text-xs text-slate-600 mt-3 pt-3 border-t border-slate-200">
                <strong className="text-slate-800">Technician notes:</strong> {wo.completion_certificate.work_performed}
              </div>
            )}
            {wo.completion_certificate?.warranty_provided && (
              <Badge className="mt-2 bg-emerald-100 text-emerald-700 border-emerald-200">
                Warranty: {wo.completion_certificate.warranty_provided}
              </Badge>
            )}
          </div>
        )}

        {/* Checklist */}
        {checklist.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h2 className="text-sm font-bold text-slate-900 mb-3">Tasks completed</h2>
            <ul className="space-y-1.5">
              {checklist.map((t) => (
                <li key={t.id} className="flex items-center gap-2 text-sm">
                  {t.completed ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <div className="w-4 h-4 rounded-full border-2 border-slate-300" />
                  )}
                  <span className={t.completed ? "text-slate-800" : "text-slate-500 italic"}>
                    {t.label}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Updates */}
        {updates.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h2 className="text-sm font-bold text-slate-900 mb-3">Activity timeline</h2>
            <ul className="space-y-3">
              {updates.map((u) => (
                <li key={u.id} className="text-sm border-l-2 border-blue-200 pl-3">
                  <div className="text-xs text-slate-500">
                    <strong className="text-slate-700">{u.by_name}</strong> &middot; {new Date(u.created_at).toLocaleString()}
                  </div>
                  <p className="text-slate-800 mt-0.5 whitespace-pre-wrap">{u.message}</p>
                  {(u.photos?.length || u.attachments?.length) > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(u.photos || []).map((p, i) => (
                        <a key={i} href={`${API}${p}`} target="_blank" rel="noreferrer">
                          <img
                            src={`${API}${p}`}
                            alt="update"
                            className="w-16 h-16 object-cover rounded border border-slate-200"
                          />
                        </a>
                      ))}
                      {(u.attachments || []).map((a, i) => (
                        <a
                          key={i}
                          href={`${API}${a.file_url}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline border border-slate-200 rounded px-2 py-1"
                        >
                          <Paperclip className="w-3 h-3" /> {a.file_name}
                        </a>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Decision form */}
        <div className="bg-white rounded-xl border-2 border-emerald-200 p-6" data-testid="signoff-decision-card">
          <h2 className="text-lg font-bold text-slate-900 mb-2">Your sign-off</h2>
          <p className="text-sm text-slate-600 mb-4">
            Please confirm the work is complete to your satisfaction. If anything is wrong, choose
            "Dispute / request changes" instead and tell us what needs fixing.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <div>
              <Label className="text-xs">Your name *</Label>
              <Input
                value={signedName}
                onChange={(e) => setSignedName(e.target.value)}
                placeholder="Jane Doe"
                data-testid="signoff-name"
              />
            </div>
            <div>
              <Label className="text-xs">Your title (optional)</Label>
              <Input
                value={signedTitle}
                onChange={(e) => setSignedTitle(e.target.value)}
                placeholder="Facilities Director"
                data-testid="signoff-title"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              onClick={() => submit("approve")}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700 flex-1 h-11"
              data-testid="signoff-approve-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Approve &amp; close work order
            </Button>
            <Button
              onClick={() => {
                if (!feedback.trim()) {
                  toast.error("Tell us what's wrong first (feedback box below)");
                  return;
                }
                submit("dispute");
              }}
              disabled={submitting}
              variant="outline"
              className="border-rose-300 text-rose-700 hover:bg-rose-50 flex-1 h-11"
              data-testid="signoff-dispute-btn"
            >
              <XCircle className="w-4 h-4 mr-2" /> Dispute / request changes
            </Button>
          </div>

          <div className="mt-4">
            <Label className="text-xs">Feedback (required if disputing)</Label>
            <Textarea
              rows={3}
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="What needs to be fixed?"
              data-testid="signoff-feedback"
            />
          </div>
        </div>

        <p className="text-xs text-slate-400 text-center mt-4">
          Powered by Sterile Mate &middot; <a href="https://sterilemate.com" className="hover:text-blue-600">sterilemate.com</a>
        </p>
      </div>
    </div>
  );
}
